/**
 * Customer Support Form API Integration
 * 客户支持表单 API 对接
 */

(function() {
  'use strict';

  // Validate against the same regional gateway as the support submission.
  async function validateCustomerSupportSn(sn) {
    const normalizedSn = String(sn || '').trim().toUpperCase();
    if (normalizedSn.length !== 16) return { ok: false };
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const apiBase = (window.API_BASE_URL || '').replace(/\/$/, '');
      const response = await fetch(`${apiBase}/user/channel/customer-support/sn/validate?sn=${encodeURIComponent(normalizedSn)}`, {
        method: 'GET',
        headers: { 'X-Client-Type': 'web', Accept: 'application/json' },
        signal: controller.signal,
        cache: 'no-store'
      });
      if (!response.ok) return { ok: false };
      const result = await response.json();
      if (!result || (result.code !== undefined && result.code !== 200)) return { ok: false };
      const data = result.data ?? result;
      if (typeof data.exists !== 'boolean' || typeof data.sn !== 'string' || data.sn.trim().toUpperCase() !== normalizedSn) {
        return { ok: false };
      }
      return { ok: true, exists: data.exists, sn: normalizedSn };
    } catch (_) {
      return { ok: false };
    } finally {
      clearTimeout(timeout);
    }
  }

  /**
   * 提交客户支持表单数据到后端 API
   * @param {Object} formData - 表单数据
   * @param {string} formData.firstName - 名字
   * @param {string} formData.lastName - 姓氏
   * @param {string} formData.email - 邮箱
   * @param {string} formData.inquiryType - 咨询类别键值，如 "PRODUCT_ORDER_INQUIRIES"
   * @param {string} formData.areaCode - 国际区号
   * @param {string} formData.phone - 联系电话（用户原始输入）
   * @param {string} formData.formatPhone - 格式化后的手机号
   * @param {string} formData.message - 详细描述
   * @returns {Promise<Object>} API 响应结果
   */
  async function submitCustomerSupport(formData) {
    const requestData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      country: formData.country || '',
      areaCode: formData.areaCode || '',
      phone: formData.phone || '',
      formatPhone: formData.formatPhone || '',
      inquiryType: formData.inquiryType,
      message: formData.message
    };

    if (formData.inquiryType === 'AFTER_SALES_SUPPORT') {
      requestData.purchaseChannel = formData.purchaseChannel;
      requestData.channelDetail = formData.channelDetail;
      requestData.afterSalesIssueType = formData.afterSalesIssueType;
      requestData.sn = formData.sn;
    } else {
      requestData.zipCode = formData.zipCode || '';
    }

    const apiBase = window.API_BASE_URL || '';
    let response;

    try {
      response = await fetch(`${apiBase}/user/channel/customer-support`, {
        method: 'POST',
        headers: {
          'X-Client-Type': 'web',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
    } catch (error) {
      // 网络超时或其他网络错误
      console.error('Customer Support API Error:', error);
      return { code: 0, message: '' };
    }

    const result = await response.json().catch(() => null);

    // 非 200 状态码或没有返回数据
    if (!response || !response.ok) {
      const errorMessage = result?.message || '';
      return { code: response?.status || 0, message: errorMessage };
    }

    // 200 状态码，返回成功结果
    if (!result) {
      return { code: 0, message: '' };
    }

    return { code: 200, ...result };
  }

  /**
   * Submit Partnership Form Data
   * @param {Object} payload - Form data payload
   * @returns {Promise<Object>} API response
   */
  async function submitPartnershipForm(payload) {
    const apiBase = window.API_BASE_URL || '';
    let response;

    try {
      response = await fetch(`${apiBase}/user/channel/partnership`, {
        method: 'POST',
        headers: {
          'X-Client-Type': 'web',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
    } catch (error) {
      console.error('Partnership API Error:', error);
      return { ok: false, errorType: 'network', message: 'Submission failed. Please try again.' };
    }

    let result;
    try {
       result = await response.json();
    } catch (e) {
       result = { errorType: 'invalid_response', message: 'Network error or invalid response.' };
    }

    if (!response || !response.ok) {
      return { ok: false, message: result?.message || 'Submission failed. Please try again.', ...result };
    }
    
    return { ok: true, ...result };
  }

  // 将函数挂载到全局 window 对象
  window.submitCustomerSupport = submitCustomerSupport;
  window.validateCustomerSupportSn = validateCustomerSupportSn;
  window.submitPartnershipForm = submitPartnershipForm;

})();
